import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DEFAULT_COLOR_RGBA, PAYMENT_ZONE_COLORS } from "../parking-payment.const";
import { FileIcon, MapPinPlusIcon, PenLineIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Tooltip } from "@/components/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HexColorInput, RgbaColor, RgbaColorPicker } from "react-colorful";
import { convertHexToRgba, convertRgbaToHex, formatArrayToRgbaColor } from "@/helper/utils";

type PaymentZoneCreationFormProps = {
  mode?: "create" | "edit";
  name: string;
  color: [number, number, number, number];
  isSaving?: boolean;
  isDrawingDraft?: boolean;
  onNameChange: (value: string) => void;
  onColorSelect: (color: [number, number, number, number]) => void;
  onCancel: () => void;
  onSubmit: () => void;
  onAddPolygon?: () => void;
  onOpenImport?: () => void;
};

export const PaymentZoneCreationForm = ({
  mode = "create",
  name,
  color = DEFAULT_COLOR_RGBA,
  isSaving = false,
  isDrawingDraft = false,
  onNameChange,
  onColorSelect,
  onCancel,
  onSubmit,
  onAddPolygon,
  onOpenImport,
}: PaymentZoneCreationFormProps) => {
  const [showAllColors, setShowAllColors] = useState(false);
  const isReady = name.trim().length > 0 && !isSaving;
  const isEditingMode = mode === "edit";
  const badgeLabel = isEditingMode ? "Edition" : "Dessin";
  const titleLabel = isEditingMode ? "Modifier la zone" : "Nouvelle zone";
  const primaryButtonLabel = isEditingMode ? "Enregistrer" : "Commencer";

  const INITIAL_COLOR_COUNT = 10;
  const displayedColors = showAllColors ? PAYMENT_ZONE_COLORS : PAYMENT_ZONE_COLORS.slice(0, INITIAL_COLOR_COUNT);
  const hasMoreColors = PAYMENT_ZONE_COLORS.length > INITIAL_COLOR_COUNT;

  const isPresetColor = PAYMENT_ZONE_COLORS.some((paletteOption) => paletteOption.rgba.every((v, i) => v === color[i]));

  const handleCustomColorChange = (value: RgbaColor) => {
    onColorSelect([value.r, value.g, value.b, Math.round((value.a ?? 1) * 255)]);
  };

  const handleCustomHexChange = (hex: string) => {
    handleCustomColorChange(convertHexToRgba(hex));
  };

  const importButton = onOpenImport ? (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="w-full gap-2"
      onClick={onOpenImport}
      disabled={isSaving}
    >
      <FileIcon className="h-4 w-4" />
      Importer un GeoJSON
    </Button>
  ) : null;

  return (
    <div className="bg-[#FBFBFB] px-5 py-4 border-b border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PenLineIcon className="h-4 w-4 text-foreground" />
          <span className="text-sm font-semibold text-foreground">{titleLabel}</span>
        </div>
        <Badge variant="outline">{badgeLabel}</Badge>
      </div>
      <div>
        <label className="text-xs font-semibold text-muted-foreground" htmlFor="payment-zone-name-input">
          Nom de la zone
        </label>
        <Input
          id="payment-zone-name-input"
          placeholder="Ex. Quartier Nord"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          className="mt-2"
          disabled={isSaving}
        />
      </div>
      <div className="mt-4">
        <p className="text-xs font-semibold text-muted-foreground">Sélectionnez une couleur</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {displayedColors.map((paletteOption, idx) => {
            const rgba = paletteOption.rgba;
            const rgbaString = `rgba(${rgba[0]},${rgba[1]},${rgba[2]},${rgba[3] / 255})`;
            const isSelected = color.every((v, i) => v === rgba[i]);
            return (
              <Tooltip key={idx} label={paletteOption.name} pos="top">
                <Button
                  type="button"
                  onClick={() => onColorSelect(rgba)}
                  className={cn(
                    "flex h-8 w-8 min-h-8 min-w-8 items-center justify-center rounded-full border-2 p-0 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isSelected ? "border-foreground" : "border-transparent",
                    isSaving && "cursor-not-allowed opacity-60",
                  )}
                  style={{ backgroundColor: rgbaString }}
                  aria-label={`Choisir la couleur ${paletteOption.name}`}
                  disabled={isSaving}
                />
              </Tooltip>
            );
          })}
          <Tooltip label="Autre couleur" pos="top">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  className={cn(
                    "flex h-8 w-8 min-h-8 min-w-8 items-center justify-center rounded-full border-2 p-0 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    !isPresetColor ? "border-foreground" : "border-dashed border-muted-foreground",
                    isSaving && "cursor-not-allowed opacity-60",
                  )}
                  style={{
                    background: isPresetColor
                      ? "conic-gradient(from 0deg, red, yellow, lime, cyan, blue, magenta, red)"
                      : `rgba(${color[0]},${color[1]},${color[2]},${color[3] / 255})`,
                  }}
                  aria-label="Choisir une autre couleur"
                  disabled={isSaving}
                />
              </PopoverTrigger>
              <PopoverContent className="w-auto space-y-2" align="start">
                <RgbaColorPicker color={formatArrayToRgbaColor(color)} onChange={handleCustomColorChange} />
                <HexColorInput
                  className="w-full rounded-md border border-input px-2 py-1 text-sm text-center"
                  alpha
                  prefixed
                  color={convertRgbaToHex(formatArrayToRgbaColor(color))}
                  onChange={handleCustomHexChange}
                />
              </PopoverContent>
            </Popover>
          </Tooltip>
        </div>
        {hasMoreColors && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setShowAllColors(!showAllColors)}
            className="mt-2 text-xs text-muted-foreground hover:text-foreground"
            disabled={isSaving}
          >
            {showAllColors ? "Voir moins" : "Voir plus"}
          </Button>
        )}
      </div>
      {!isDrawingDraft && !isEditingMode && (
        <div className="mt-6 space-y-3">
          {importButton}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Annuler
            </Button>
            <Button size="sm" onClick={onSubmit} disabled={!isReady}>
              {primaryButtonLabel}
            </Button>
          </div>
        </div>
      )}
      {isEditingMode && onAddPolygon && (
        <div className="mt-6 space-y-2">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isSaving || !isDrawingDraft}
              className={cn("flex-1", !isDrawingDraft && "bg-[#E5E5E5] hover:bg-[#E5E5E5] cursor-default")}
            >
              Modifier
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddPolygon}
              disabled={isSaving || isDrawingDraft}
              className={cn("flex-1 gap-1.5", isDrawingDraft && "bg-[#E5E5E5] hover:bg-[#E5E5E5] cursor-default")}
            >
              <MapPinPlusIcon className="h-4 w-4" />
              Ajouter
            </Button>
          </div>
          {importButton}
        </div>
      )}
    </div>
  );
};
